import React, { memo, useEffect, useState } from "react";
import {
  Button,
  Label,
  Modal,
  ModalBody,
  ModalHeader,
  Spinner,
} from "reactstrap";
import {
  fetchNotification,
  resendNotification,
} from "services/services.projects";
import { Toast } from "utility/helpers";
import { InputTags } from "react-bootstrap-tagsinput";
import PropTypes from "prop-types";
import useAuth from "hooks/useAuth";
import { Check } from "react-feather";
import styled from "styled-components";
import { AddButton } from "assets/js/Styles";
import FormGenerator from "components/customs/form-generator/input";
import CustomCheckbox from "../../../../components/customs/checkbox";
import LoadingScreen from "../../../../components/customs/loading-screen";

import "react-bootstrap-tagsinput/dist/index.css";

const Container = styled.div`
  padding: 0;
  margin-bottom: 1rem;
`;

const CheckboxContainer = styled.div`
  margin-top: 0.5rem;
`;

const TextareaContainer = styled.div`
  margin-top: 1rem;
`;

const ButtonActionSection = styled.div`
  display: flex;
  justify-content: flex-end;
`;

const InputGroup = styled.div`
  background: white;

  input {
    display: none;
  }
`;

const DeleteButton = styled(Button)`
  color: ${({ theme }) => theme.colors.secondaryRed};
  border-color: ${({ theme }) => theme.colors.secondaryRed};
`;

const StyledModal = styled(Modal)`
  .badge {
    background-color: ${({ theme }) => theme.colors.secondaryBlue} !important;
    margin-bottom: 6px;

    button {
      margin-left: 2px;
    }
  }
`;

const ModalBodyContainer = styled.div`
  margin-right: 0.5rem;
  margin-left: 0.5rem;
`;

const ResendModal = ({ isOpen, data, handleClose, type, messageType }) => {
  const { user } = useAuth();

  const [recipients, setRecipients] = useState([]);
  const [description, setDescription] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [sendToCOS, setSendToCOS] = useState(true);

  const renderRecipients = () => {
    let element = "";

    if (type === "single") {
      element = recipients.map((recipient) => {
        return (
          <>
            <label htmlFor={recipient.id}>{"Recipients"}</label>

            <FormGenerator
              type="text"
              key={recipient.id}
              disabled
              value={recipient.name}
            />
          </>
        );
      });
    } else {
      const defaultValue = [
        ...new Set(recipients.map((recipient) => recipient.name)),
      ];

      element = (
        <>
          <label htmlFor="multiple-badges">{"Recipients"}</label>
          <InputGroup className="input-group">
            <InputTags
              id="multiple-badges"
              values={defaultValue}
              onTags={({ values }) => {
                const newData = [];

                // eslint-disable-next-line array-callback-return
                recipients.map((recipient) => {
                  if (values.includes(recipient.name)) newData.push(recipient);
                });

                setRecipients(newData);
              }}
              disabled
            />
            <DeleteButton
              type="button"
              outline
              data-testid="button-clearAll"
              disabled={isLoading}
              onClick={() => {
                setRecipients([]);
              }}
            >
              {"Delete"}
            </DeleteButton>
          </InputGroup>
        </>
      );
    }

    return element;
  };

  const fetchSentNotification = () => {
    fetchNotification({
      url: `/recipient/${data[0]?.id}/latest-sent-notification`,
      callback: (response) => {
        if (response.notification && response.notification.message) {
          const { notification } = response;
          const { message } = notification;
          setDescription(message);
        }

        setIsLoading(false);
      },
    });
  };

  const sendData = (formData) => {
    resendNotification({
      formData,
      callback: ({ isSuccess }) => {
        if (isSuccess) {
          handleClose();
          setSendToCOS(true);

          Toast({
            type: 1,
            content: "Successfully resent message!.",
          });
        } else {
          Toast({
            type: 4,
            content: "An error occurred when resending messages.",
          });
        }

        setIsLoading(false);
      },
    });
  };

  const handleConfirmClicked = () => {
    if (recipients.length === 0) {
      return Toast({
        type: 4,
        content: "Recipients required.",
      });
    }

    if (description === "" && messageType !== "approval") {
      return Toast({
        type: 4,
        content: "Message required.",
      });
    }

    setIsLoading(true);
    const contact = recipients.map((recipient) => recipient.contact).toString();
    const { status } = recipients[0];
    const congs = recipients.map((recipient) => recipient.id);

    const notification = {
      message: description,
      contact,
      status,
      recipients: congs,
    };

    const formData = {
      notification,
      userId: user?.userId,
      messageType:
        messageType === "approval" ? "Approval Message" : "General Message",
      includeCOS: sendToCOS,
    };

    return sendData(formData);
  };

  useEffect(() => {
    if (data.length !== 0) {
      const recipientsData = data.map((recipient) => {
        return {
          // eslint-disable-next-line max-len
          name: `${recipient.recipient.lastName}, ${recipient.recipient.firstName}`,
          id: recipient.id,
          contact: recipient.recipient.contact,
          status: recipient.status,
        };
      });

      setRecipients(recipientsData);
      setDescription("");

      if (type === "single") {
        setIsLoading(true);
        fetchSentNotification();
      }
    }
  }, [data]);

  if (!user) {
    return <LoadingScreen />;
  }

  return (
    <StyledModal
      isOpen={isOpen}
      toggle={handleClose}
      centered
    >
      <ModalHeader toggle={handleClose}>{"Resend Message"}</ModalHeader>
      <ModalBody>
        <ModalBodyContainer>
          <Container>
            {renderRecipients()}
            <CheckboxContainer>
              <Label check>
                <CustomCheckbox
                  icon={<Check size={16} />}
                  checked={sendToCOS}
                  onChange={(e) => {
                    const { checked } = e.target;
                    setSendToCOS(checked);
                  }}
                  type="checkbox"
                  label="Send to Chief of Staff"
                />
              </Label>
            </CheckboxContainer>

            {(type === "single" || messageType !== "approval") && (
              <TextareaContainer>
                <FormGenerator
                  type="textarea"
                  rows={5}
                  value={description}
                  disabled={isLoading}
                  onChange={(e) => {
                    const { value } = e.target;

                    setDescription(value);
                  }}
                />
              </TextareaContainer>
            )}
          </Container>

          <ButtonActionSection>
            <AddButton
              onClick={() => handleConfirmClicked()}
              disabled={isLoading}
            >
              {isLoading ? (
                <Spinner
                  animation="border"
                  color="light"
                  size="sm"
                />
              ) : (
                "Confirm"
              )}
            </AddButton>
          </ButtonActionSection>
        </ModalBodyContainer>
      </ModalBody>
    </StyledModal>
  );
};

ResendModal.prototype = {
  isOpen: PropTypes.bool,
  data: PropTypes.oneOfType([PropTypes.string]),
  handleClose: PropTypes.func,
  type: PropTypes.string,
  messageType: PropTypes.string,
};

export default memo(ResendModal);
